/**
 * Backfill Preview service for SubContractorHub (Story SCH-3.1).
 *
 * Wraps the single `POST /preview-backfill` endpoint. Mirrors
 * documentFilterService conventions: shared fetchJson helper, snake↔camel
 * translation at the boundary, discriminated union result type.
 *
 * No caching at this layer — preview is one-shot per click; the hook layer
 * uses `useMutation` (not `useQuery`).
 */

import { supabase } from '@/lib/supabaseClient';
import type {
  AdderSnapshotItem,
  BackfillPreviewResponse,
  BackfillPreviewResult,
  MappingWarning,
  MappingWarningKind,
  ProjectedState,
  ProjectPreview,
  StageProjection,
} from '@/types/integrations/backfillPreview';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function fetchJson<TData>(
  path: string,
  init: RequestInit,
): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...init,
    headers: { ...headers, ...(init.headers ?? {}) },
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

// ─── Raw → camelCase mappers ─────────────────────────────────────────────

interface RawMappingWarning {
  kind: MappingWarningKind;
  message: string;
  source_field: string | null;
  target_field: string | null;
}

function _mapWarning(raw: RawMappingWarning): MappingWarning {
  return {
    kind: raw.kind,
    message: raw.message,
    sourceField: raw.source_field ?? null,
    targetField: raw.target_field ?? null,
  };
}

interface RawStageProjection {
  id: number;
  name: string;
  pipeline_id: number;
  pipeline_name: string;
}

function _mapStage(raw: RawStageProjection | null): StageProjection | null {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    pipelineId: raw.pipeline_id,
    pipelineName: raw.pipeline_name,
  };
}

interface RawAdderSnapshot {
  adder_id: number;
  name_snapshot: string;
  unit_price_snapshot: number | null;
  quantity: number | null;
  total_cost_snapshot: number | null;
}

function _mapAdder(raw: RawAdderSnapshot): AdderSnapshotItem {
  return {
    adderId: raw.adder_id,
    nameSnapshot: raw.name_snapshot,
    unitPriceSnapshot: raw.unit_price_snapshot,
    quantity: raw.quantity,
    totalCostSnapshot: raw.total_cost_snapshot,
  };
}

interface RawProjectedState {
  project_name: string | null;
  address: string | null;
  stage: RawStageProjection | null;
  equipment: Record<string, unknown>;
  adders_snapshot: RawAdderSnapshot[];
  owner_user_id: string | null;
  document_count: number;
}

function _mapProjected(raw: RawProjectedState): ProjectedState {
  return {
    projectName: raw.project_name,
    address: raw.address,
    stage: _mapStage(raw.stage),
    equipment: raw.equipment ?? {},
    addersSnapshot: (raw.adders_snapshot ?? []).map(_mapAdder),
    ownerUserId: raw.owner_user_id,
    documentCount: raw.document_count,
  };
}

interface RawProjectPreview {
  subhub_id: string;
  subhub_name: string;
  projected: RawProjectedState;
  mapping_warnings: RawMappingWarning[];
}

function _mapPreview(raw: RawProjectPreview): ProjectPreview {
  return {
    subhubId: raw.subhub_id,
    subhubName: raw.subhub_name,
    projected: _mapProjected(raw.projected),
    mappingWarnings: (raw.mapping_warnings ?? []).map(_mapWarning),
  };
}

interface RawBackfillPreviewResponse {
  projects: RawProjectPreview[];
  total_fetched: number;
  error: 'sch_unreachable' | null;
  generated_at: string;
}

function _mapResponse(raw: RawBackfillPreviewResponse): BackfillPreviewResponse {
  return {
    projects: (raw.projects ?? []).map(_mapPreview),
    totalFetched: raw.total_fetched,
    error: raw.error ?? null,
    generatedAt: raw.generated_at,
  };
}

// ─── Error normalization ────────────────────────────────────────────────

const PREVIEW_ERROR_CODES = [
  'auth_failed',
  'integration_disabled',
  'integration_not_found',
  'sch_unreachable',
  'internal_error',
] as const;
type PreviewErrorCode = (typeof PREVIEW_ERROR_CODES)[number];

function _normalizeError(
  raw: { code: string; message: string; details?: Record<string, unknown> } | null,
): { code: PreviewErrorCode; message: string; details?: Record<string, unknown> } {
  if (!raw) {
    return { code: 'internal_error', message: 'Unknown error' };
  }
  const code = (PREVIEW_ERROR_CODES as readonly string[]).includes(raw.code)
    ? (raw.code as PreviewErrorCode)
    : ('internal_error' as PreviewErrorCode);
  return { code, message: raw.message, details: raw.details };
}

// ─── Public API ─────────────────────────────────────────────────────────

async function runBackfillPreview(
  options: { search?: string } = {},
): Promise<BackfillPreviewResult> {
  const search = options.search?.trim();
  const path = search
    ? `/api/v1/integrations/subcontractorhub/preview-backfill?search=${encodeURIComponent(search)}`
    : '/api/v1/integrations/subcontractorhub/preview-backfill';

  const envelope = await fetchJson<RawBackfillPreviewResponse>(path, {
    method: 'POST',
  });

  if (envelope.error) {
    return { data: null, error: _normalizeError(envelope.error) };
  }
  if (!envelope.data) {
    return {
      data: null,
      error: { code: 'internal_error', message: 'Empty response from backend' },
    };
  }
  return { data: _mapResponse(envelope.data), error: null };
}

export const backfillPreviewService = {
  runBackfillPreview,
};
